/**
 * Gongfu 模板系统
 * 提供工作流模板的管理、创建和应用
 */

import { EventEmitter } from 'events';
import * as fs from 'fs-extra';
import * as path from 'path';
import * as yaml from 'js-yaml';
import { logger } from '../utils/logger';
import { ExtendedTask } from '../types/extended-task';
import { Agent } from './agent-manager';
import { PromptTemplate } from './prompt-template-engine';

/**
 * 模板元数据
 */
export interface TemplateMetadata {
  id: string;
  name: string;
  description: string;
  version: string;
  author?: string;
  category: TemplateCategory;
  tags?: string[];
  difficulty?: 'beginner' | 'intermediate' | 'advanced';
  estimatedTime?: string;
  prerequisites?: string[];
  outcomes?: string[];
  thumbnail?: string;
  downloads?: number;
  rating?: number;
  verified?: boolean;
}

/**
 * 模板分类
 */
export type TemplateCategory = 
  | 'web-development'      // Web 开发
  | 'mobile-development'   // 移动开发
  | 'backend-development'  // 后端开发
  | 'devops'              // DevOps
  | 'data-science'        // 数据科学
  | 'machine-learning'    // 机器学习
  | 'documentation'       // 文档编写
  | 'testing'            // 测试
  | 'project-management' // 项目管理
  | 'open-source'        // 开源项目
  | 'startup'           // 创业项目
  | 'education';        // 教育培训

/**
 * 工作流模板
 */
export interface WorkflowTemplate {
  metadata: TemplateMetadata;
  
  // 工作流定义
  workflow: {
    name: string;
    description: string;
    phases: WorkflowPhase[];
    variables?: TemplateVariable[];
    hooks?: WorkflowHooks;
  };
  
  // 资源定义
  resources?: {
    agents?: Agent[];
    prompts?: PromptTemplate[];
    documents?: DocumentTemplate[];
    configs?: ConfigTemplate[];
  };
  
  // 示例数据
  examples?: {
    tasks?: Partial<ExtendedTask>[];
    outputs?: any[];
  };
}

/**
 * 工作流阶段
 */
export interface WorkflowPhase {
  id: string;
  name: string;
  description: string;
  tasks: TaskTemplate[];
  dependencies?: string[];
  estimatedTime?: string;
  deliverables?: string[];
}

/**
 * 任务模板
 */
export interface TaskTemplate {
  id: string;
  content: string;
  type?: string;
  priority?: string;
  tags?: string[];
  agentRole?: string;
  promptTemplate?: string;
  variables?: Record<string, any>;
  subtasks?: TaskTemplate[];
}

/**
 * 模板变量
 */
export interface TemplateVariable {
  name: string;
  type: 'string' | 'number' | 'boolean' | 'array' | 'object';
  description: string;
  required?: boolean;
  default?: any;
  choices?: any[];
  validation?: string; // 正则表达式
}

/**
 * 工作流钩子
 */
export interface WorkflowHooks {
  beforeStart?: string;    // 脚本路径
  afterComplete?: string;
  onPhaseStart?: string;
  onPhaseComplete?: string;
  onTaskCreate?: string;
  onError?: string;
}

/**
 * 文档模板
 */
export interface DocumentTemplate {
  name: string;
  path: string;
  template: string;
  variables?: string[];
}

/**
 * 配置模板
 */
export interface ConfigTemplate {
  name: string;
  path: string;
  content: any;
}

/**
 * 模板应用上下文
 */
export interface TemplateContext {
  projectName: string;
  projectPath: string;
  variables: Record<string, any>;
  options?: {
    skipDocs?: boolean;
    skipConfigs?: boolean;
    autoStart?: boolean;
    interactive?: boolean;
  };
}

/**
 * 模板管理器
 */
export class TemplateManager extends EventEmitter {
  private templates: Map<string, WorkflowTemplate> = new Map();
  private templatesDir: string;
  private userTemplatesDir: string;
  private communityTemplatesDir: string;
  
  constructor(
    private workflowDir: string = process.cwd()
  ) {
    super();
    this.templatesDir = path.join(__dirname, '../../templates');
    this.userTemplatesDir = path.join(workflowDir, '.workflow', 'templates');
    this.communityTemplatesDir = path.join(workflowDir, '.workflow', 'community-templates');
  }
  
  /**
   * 初始化模板系统
   */
  async initialize(): Promise<void> {
    // 确保目录存在
    await fs.ensureDir(this.userTemplatesDir);
    await fs.ensureDir(this.communityTemplatesDir);
    
    // 加载模板
    await this.loadTemplates();
    
    logger.info(`Template system initialized with ${this.templates.size} templates`);
  }
  
  /**
   * 加载所有模板
   */
  private async loadTemplates(): Promise<void> {
    // 加载内置模板
    await this.loadTemplatesFromDir(this.templatesDir, 'built-in');
    
    // 加载用户模板
    await this.loadTemplatesFromDir(this.userTemplatesDir, 'user');
    
    // 加载社区模板
    await this.loadTemplatesFromDir(this.communityTemplatesDir, 'community');
  }
  
  /**
   * 从目录加载模板
   */
  private async loadTemplatesFromDir(dir: string, source: string): Promise<void> {
    if (!await fs.pathExists(dir)) {
      return;
    }
    
    try {
      const entries = await fs.readdir(dir, { withFileTypes: true });
      
      for (const entry of entries) {
        if (entry.isDirectory()) {
          const templatePath = path.join(dir, entry.name);
          await this.loadTemplate(templatePath, source);
        }
      }
    } catch (error) {
      logger.error(`Failed to load templates from ${dir}:`, error);
    }
  }
  
  /**
   * 加载单个模板
   */
  private async loadTemplate(templatePath: string, source: string): Promise<void> {
    try {
      const metadataPath = path.join(templatePath, 'template.yaml');
      if (!await fs.pathExists(metadataPath)) {
        return;
      }
      
      // 读取模板元数据
      const content = await fs.readFile(metadataPath, 'utf-8');
      const template = yaml.load(content) as WorkflowTemplate;
      
      // 添加来源信息
      template.metadata.id = template.metadata.id || path.basename(templatePath);
      
      // 注册模板
      this.templates.set(template.metadata.id, template);
      
      logger.debug(`Loaded template: ${template.metadata.name} from ${source}`);
    } catch (error) {
      logger.error(`Failed to load template from ${templatePath}:`, error);
    }
  }
  
  /**
   * 获取所有模板
   */
  getTemplates(category?: TemplateCategory): WorkflowTemplate[] {
    const templates = Array.from(this.templates.values());
    
    if (category) {
      return templates.filter(t => t.metadata.category === category);
    }
    
    return templates;
  }
  
  /**
   * 获取模板
   */
  getTemplate(id: string): WorkflowTemplate | undefined {
    return this.templates.get(id);
  }
  
  /**
   * 应用模板
   */
  async applyTemplate(
    templateId: string,
    context: TemplateContext
  ): Promise<ApplyTemplateResult> {
    const template = this.templates.get(templateId);
    if (!template) {
      throw new Error(`Template not found: ${templateId}`);
    }
    
    logger.info(`Applying template: ${template.metadata.name}`);
    
    // 验证变量
    this.validateVariables(template, context.variables);
    
    // 创建项目结构
    await this.createProjectStructure(context.projectPath);
    
    const result: ApplyTemplateResult = {
      projectPath: context.projectPath,
      tasks: [],
      documents: [],
      configs: [],
      errors: []
    };
    
    try {
      // 应用资源
      if (template.resources && !context.options?.skipConfigs) {
        await this.applyResources(template.resources, context, result);
      }
      
      // 创建任务
      await this.createTasks(template.workflow, context, result);
      
      // 创建文档
      if (template.resources?.documents && !context.options?.skipDocs) {
        await this.createDocuments(template.resources.documents, context, result);
      }
      
      // 触发事件
      this.emit('template:applied', {
        templateId,
        context,
        result
      });
      
      logger.info(`Template applied successfully: ${result.tasks.length} tasks created`);
    } catch (error) {
      logger.error('Failed to apply template:', error);
      result.errors.push(error.message);
    }
    
    return result;
  }
  
  /**
   * 验证变量
   */
  private validateVariables(
    template: WorkflowTemplate,
    variables: Record<string, any>
  ): void {
    if (!template.workflow.variables) {
      return;
    }
    
    for (const varDef of template.workflow.variables) {
      const value = variables[varDef.name];
      
      // 检查必填
      if (varDef.required && value === undefined) {
        throw new Error(`Required variable missing: ${varDef.name}`);
      }
      
      // 检查类型
      if (value !== undefined) {
        const actualType = Array.isArray(value) ? 'array' : typeof value;
        if (actualType !== varDef.type) {
          throw new Error(`Variable ${varDef.name} type mismatch: expected ${varDef.type}, got ${actualType}`);
        }
        
        // 检查选项
        if (varDef.choices && !varDef.choices.includes(value)) {
          throw new Error(`Variable ${varDef.name} value not in choices: ${value}`);
        }
        
        // 正则验证
        if (varDef.validation) {
          const regex = new RegExp(varDef.validation);
          if (!regex.test(String(value))) {
            throw new Error(`Variable ${varDef.name} validation failed: ${value}`);
          }
        }
      }
    }
  }
  
  /**
   * 创建项目结构
   */
  private async createProjectStructure(projectPath: string): Promise<void> {
    await fs.ensureDir(projectPath);
    await fs.ensureDir(path.join(projectPath, '.workflow'));
    await fs.ensureDir(path.join(projectPath, 'src'));
    await fs.ensureDir(path.join(projectPath, 'docs'));
    await fs.ensureDir(path.join(projectPath, 'tests'));
  }
  
  /**
   * 应用资源
   */
  private async applyResources(
    resources: WorkflowTemplate['resources'],
    context: TemplateContext,
    result: ApplyTemplateResult
  ): Promise<void> {
    // TODO: 实现资源应用
    // - 注册 Agents
    // - 注册 Prompts
    // - 复制配置文件
  }
  
  /**
   * 创建任务
   */
  private async createTasks(
    workflow: WorkflowTemplate['workflow'],
    context: TemplateContext,
    result: ApplyTemplateResult
  ): Promise<void> {
    const allTasks: ExtendedTask[] = [];
    
    // 按阶段创建任务
    for (const phase of workflow.phases) {
      const phaseTasks = await this.createPhaseTasks(phase, context);
      allTasks.push(...phaseTasks);
    }
    
    // 保存任务
    const tasksPath = path.join(context.projectPath, '.workflow', 'tasks.json');
    await fs.writeJson(tasksPath, allTasks, { spaces: 2 });
    
    result.tasks = allTasks;
  }
  
  /**
   * 创建阶段任务
   */
  private async createPhaseTasks(
    phase: WorkflowPhase,
    context: TemplateContext
  ): Promise<ExtendedTask[]> {
    const tasks: ExtendedTask[] = [];
    
    for (const taskTemplate of phase.tasks) {
      const task = this.createTaskFromTemplate(taskTemplate, phase, context);
      tasks.push(task);
      
      // 创建子任务
      if (taskTemplate.subtasks) {
        for (const subtaskTemplate of taskTemplate.subtasks) {
          const subtask = this.createTaskFromTemplate(subtaskTemplate, phase, context);
          subtask.parentId = task.id;
          tasks.push(subtask);
        }
      }
    }
    
    return tasks;
  }
  
  /**
   * 从模板创建任务
   */
  private createTaskFromTemplate(
    template: TaskTemplate,
    phase: WorkflowPhase,
    context: TemplateContext
  ): ExtendedTask {
    // 替换变量
    const content = this.interpolateVariables(template.content, context.variables);
    
    const task: ExtendedTask = {
      id: `${phase.id}-${template.id}`,
      content,
      status: 'todo',
      priority: template.priority as any || 'medium',
      type: template.type,
      tags: template.tags,
      phase: phase.id,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      metadata: {
        templateId: template.id,
        phaseId: phase.id,
        variables: template.variables
      }
    };
    
    // 设置 Agent
    if (template.agentRole) {
      task.agentAssignments = {
        primary: template.agentRole
      };
    }
    
    // 设置 Prompt 模板
    if (template.promptTemplate) {
      task.promptTemplates = {
        execution: template.promptTemplate
      };
    }
    
    return task;
  }
  
  /**
   * 创建文档
   */
  private async createDocuments(
    documents: DocumentTemplate[],
    context: TemplateContext,
    result: ApplyTemplateResult
  ): Promise<void> {
    for (const docTemplate of documents) {
      try {
        const docPath = path.join(context.projectPath, docTemplate.path);
        const content = this.interpolateVariables(docTemplate.template, context.variables);
        
        await fs.ensureDir(path.dirname(docPath));
        await fs.writeFile(docPath, content, 'utf-8');
        
        result.documents.push(docPath);
      } catch (error) {
        logger.error(`Failed to create document ${docTemplate.name}:`, error);
        result.errors.push(`Document creation failed: ${docTemplate.name}`);
      }
    }
  }
  
  /**
   * 变量插值
   */
  private interpolateVariables(
    template: string,
    variables: Record<string, any>
  ): string {
    return template.replace(/\{\{(\w+)\}\}/g, (match, key) => {
      return variables[key] || match;
    });
  }
  
  /**
   * 创建新模板
   */
  async createTemplate(
    metadata: TemplateMetadata,
    workflow: WorkflowTemplate['workflow']
  ): Promise<string> {
    const templateId = metadata.id || this.generateTemplateId(metadata.name);
    const templatePath = path.join(this.userTemplatesDir, templateId);
    
    // 创建模板目录
    await fs.ensureDir(templatePath);
    
    // 创建模板文件
    const template: WorkflowTemplate = {
      metadata: {
        ...metadata,
        id: templateId,
        version: metadata.version || '1.0.0'
      },
      workflow
    };
    
    // 保存模板
    const templateFile = path.join(templatePath, 'template.yaml');
    await fs.writeFile(templateFile, yaml.dump(template), 'utf-8');
    
    // 注册模板
    this.templates.set(templateId, template);
    
    logger.info(`Created template: ${templateId}`);
    
    return templateId;
  }
  
  /**
   * 生成模板 ID
   */
  private generateTemplateId(name: string): string {
    return name.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');
  }
  
  /**
   * 导入模板
   */
  async importTemplate(sourcePath: string): Promise<string> {
    const metadataPath = path.join(sourcePath, 'template.yaml');
    if (!await fs.pathExists(metadataPath)) {
      throw new Error('Invalid template: template.yaml not found');
    }
    
    // 读取模板
    const content = await fs.readFile(metadataPath, 'utf-8');
    const template = yaml.load(content) as WorkflowTemplate;
    
    // 复制到用户模板目录
    const targetPath = path.join(this.userTemplatesDir, template.metadata.id);
    await fs.copy(sourcePath, targetPath);
    
    // 注册模板
    this.templates.set(template.metadata.id, template);
    
    return template.metadata.id;
  }
  
  /**
   * 导出模板
   */
  async exportTemplate(templateId: string, targetPath: string): Promise<void> {
    const template = this.templates.get(templateId);
    if (!template) {
      throw new Error(`Template not found: ${templateId}`);
    }
    
    const sourcePath = path.join(this.userTemplatesDir, templateId);
    if (!await fs.pathExists(sourcePath)) {
      // 如果是内置模板，从内置目录复制
      const builtInPath = path.join(this.templatesDir, templateId);
      if (await fs.pathExists(builtInPath)) {
        await fs.copy(builtInPath, targetPath);
        return;
      }
      throw new Error(`Template files not found: ${templateId}`);
    }
    
    await fs.copy(sourcePath, targetPath);
  }
  
  /**
   * 搜索模板
   */
  searchTemplates(query: string): WorkflowTemplate[] {
    const results: WorkflowTemplate[] = [];
    const queryLower = query.toLowerCase();
    
    for (const template of this.templates.values()) {
      if (
        template.metadata.name.toLowerCase().includes(queryLower) ||
        template.metadata.description.toLowerCase().includes(queryLower) ||
        template.metadata.tags?.some(tag => tag.toLowerCase().includes(queryLower))
      ) {
        results.push(template);
      }
    }
    
    return results;
  }
}

/**
 * 应用模板结果
 */
export interface ApplyTemplateResult {
  projectPath: string;
  tasks: ExtendedTask[];
  documents: string[];
  configs: string[];
  errors: string[];
}